import React from 'react';
import PropTypes from 'prop-types';
import AssetTableRow from './AssetTableRow';
import LastAction from './LastAction';
import Pagination from '../../../../Pagination/Pagination';

/**
 * @typedef {import('../../types/User').User} User
 * @typedef {import('../../types/ContentItems').ContentItem} ContentItem
 */

/**
 * Functional component for displaying assets in a table.
 * Supports "one-by-one" selection logic via onClick.
 * Renders a responsive table with asset rows, handling selection and user interactions.
 * @param {Object} props - The component props.
 * @param {ContentItem[]} props.contentItems - Array of content items to display as assets.
 * @param {number} props.numFound - Total number of content items found (for pagination).
 * @param {ContentItem[]} props.selectedAssets - Array of currently selected asset objects.
 * @param {Function} props.onClick - Callback function for handling asset selection, passed to each row.
 * @param {LastAction} props.lastAction - Last action object to show feedback.
 * @returns {JSX.Element} The rendered asset table UI.
 */
function AssetTable({ contentItems, numFound, selectedAssets, onClick, lastAction }) {
  contentItems = contentItems || [];

  return (
    <div className="asset-selection-table table">
      <table className="selections">
        <thead>
          <tr>
            <th />
            <th>Type</th>
            <th>Thumbnail</th>
            <th>Name</th>
            <th>Duration</th>
            <th>Resolution</th>
            <th>Data</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {contentItems.map(item => (
            <AssetTableRow
              key={`${item.type}-${item.id}`}
              item={item}
              isSelected={selectedAssets.some(
                asset => asset.id === item.id && asset.type === item.type,
              )}
              onClick={onClick}
              lastAction={lastAction}
            />
          ))}
        </tbody>
      </table>
      <Pagination itemsFound={numFound} />
    </div>
  );
}

AssetTable.propTypes = {
  contentItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  numFound: PropTypes.number.isRequired,
  selectedAssets: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClick: PropTypes.func.isRequired,
  lastAction: PropTypes.instanceOf(LastAction).isRequired,
};

export default AssetTable;
